import type { RunDTO, RunEventDTO } from "@roomate/core";
import type { SkillListItemDTO } from "../components/skills/skills.ts";

/** Label of the evidence an agent attaches when it proposes a skill (the never-install-skills rule). */
export const SKILL_SOURCE_LABEL = "skill-source";

/** The URL of a decision's `skill-source` evidence, or null when it has none. */
export function skillSourceUrl(evidence: readonly { label: string; ref: string }[]): string | null {
  const hit = evidence.find((e) => e.label === SKILL_SOURCE_LABEL && e.ref.trim());
  return hit ? hit.ref.trim() : null;
}

/** Skills an agent can be given: approved and not archived, plus any already assigned (so an assignment is never hidden). */
export function assignableSkills(skills: readonly SkillListItemDTO[], assigned: readonly string[]): SkillListItemDTO[] {
  return skills.filter((s) => (s.approvedVersion !== null && s.archivedAt === null) || assigned.includes(s.id)).sort((a, b) => a.name.localeCompare(b.name));
}

export type RunSkillRow = { name: string; version: number | null; loaded: boolean; keptHookIds: string[] };
export type RunSkillsView =
  | { state: "unsupported" }
  | { state: "none" }
  | { state: "shown"; sandboxed: boolean; skills: RunSkillRow[]; initSeen: boolean };

const NAMESPACE = "roomate-skills:";
const bare = (n: string) => (n.startsWith(NAMESPACE) ? n.slice(NAMESPACE.length) : n);

/**
 * The run page's Skills section, from the delivered note (`data.skills`, `data.sandboxed`) and the init
 * event's `data.loadedSkills`. Only inits after the newest delivery note count (a resume delivers again). A skill is "loaded" only when the init line reported it; otherwise it was
 * delivered but its load is not reported (never a false "loaded").
 */
export function runSkillsView(run: Pick<RunDTO, "runtime">, events: readonly RunEventDTO[]): RunSkillsView {
  if (run.runtime === "codex") return { state: "unsupported" };
  const isDelivery = (e: RunEventDTO) => {
    const skills = e.kind === "note" ? (e.data as { skills?: unknown } | null)?.skills : null;
    return Array.isArray(skills) && skills.every((s) => s && typeof s === "object" && "skillId" in s);
  };
  // The newest delivery wins (a resumed run delivers again).
  const note = events.findLast(isDelivery);
  if (!note) return { state: "none" };
  const data = note.data as { skills: { name?: unknown; version?: unknown; keptHookIds?: unknown }[]; sandboxed?: unknown };
  const inits = events.filter((e) => e.kind === "init" && e.seq > note.seq && Array.isArray((e.data as { loadedSkills?: unknown } | null)?.loadedSkills));
  const loaded = new Set(inits.flatMap((e) => ((e.data as { loadedSkills: unknown[] }).loadedSkills).filter((n): n is string => typeof n === "string").map(bare)));
  const skills = data.skills
    .filter((s) => typeof s.name === "string")
    .map((s) => ({
      name: s.name as string,
      version: typeof s.version === "number" ? s.version : null,
      loaded: loaded.has(s.name as string),
      keptHookIds: Array.isArray(s.keptHookIds) ? s.keptHookIds.filter((h): h is string => typeof h === "string") : [],
    }));
  return { state: "shown", sandboxed: data.sandboxed === true, skills, initSeen: inits.length > 0 };
}
